import React, { useState, useRef, useEffect } from 'react';
import {
    ChevronDown,
    ChevronRight,
    Search,
    MoreHorizontal,
    X,
    ChevronsLeft,
    ChevronsRight,
    ChevronLeft,
    Trash2,
    Play,
    Pause,
    Edit
} from 'lucide-react';

const SubscriptionPlansManagement = () => {
    const [activeTab, setActiveTab] = useState('Active');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedPlans, setSelectedPlans] = useState(new Set());
    const [openDropdown, setOpenDropdown] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showActivateModal, setShowActivateModal] = useState(false);
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [currentModalStep, setCurrentModalStep] = useState(1);
    const [editingPlan, setEditingPlan] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [formData, setFormData] = useState({
        planName: '',
        planDescription: '',
        duration: '',
        durationUnit: 'Weeks',
        price: '',
        currency: 'USD',
        appStoreId: '',
        playStoreId: '',
        features: {
            freeMessages: '',
            unlimitedLikes: '',
            travelMode: '',
            advancedFilters: ''
        }
    });

    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Mock data for subscription plans
    const subscriptionPlans = [
        {
            id: 1,
            planName: '1 Week Plan',
            createdOn: 'May12, 2025 10:04 AM',
            price: '$9.99',
            duration: '1 Week',
            features: 12,
            noOfUsers: 12000,
            status: 'Active'
        },
        {
            id: 2,
            planName: '1 Month Plan',
            createdOn: 'May12, 2025 10:04 AM',
            price: '$24.99',
            duration: '1 Month',
            features: 15,
            noOfUsers: 12000,
            status: 'Active'
        },
        {
            id: 3,
            planName: '6 Months Plan',
            createdOn: 'May12, 2025 10:04 AM',
            price: '$49.99',
            duration: '6 Months',
            features: 18,
            noOfUsers: 12000,
            status: 'Inactive'
        },
        {
            id: 4,
            planName: '12 Months Plan',
            createdOn: 'May12, 2025 10:04 AM',
            price: '$89.99',
            duration: '12 Months',
            features: 20,
            noOfUsers: 12000,
            status: 'Deleted'
        }
    ];

    const durationUnits = ['Weeks', 'Months', 'Years'];
    const currencies = ['USD', 'EUR', 'GBP', 'CAD'];
    const featureOptions = ['Yes', 'No', 'Limited'];

    const filteredPlans = subscriptionPlans.filter(plan => {
        const matchesTab = plan.status === activeTab;
        const matchesSearch = plan.planName.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesTab && matchesSearch;
    });

    const handleSelectAll = (checked) => {
        if (checked) {
            const allPlanIds = new Set(filteredPlans.map(plan => plan.id));
            setSelectedPlans(allPlanIds);
        } else {
            setSelectedPlans(new Set());
        }
    };

    const handleSelectPlan = (planId, checked) => {
        const newSelection = new Set(selectedPlans);
        if (checked) {
            newSelection.add(planId);
        } else {
            newSelection.delete(planId);
        }
        setSelectedPlans(newSelection);
    };

    const toggleDropdown = (planId) => {
        setOpenDropdown(openDropdown === planId ? null : planId);
    };

    const handleCreatePlan = () => {
        setEditingPlan(null);
        setFormData({
            planName: '',
            planDescription: '',
            duration: '',
            durationUnit: 'Weeks',
            price: '',
            currency: 'USD',
            appStoreId: '',
            playStoreId: '',
            features: {
                freeMessages: '',
                unlimitedLikes: '',
                travelMode: '',
                advancedFilters: ''
            }
        });
        setCurrentModalStep(1);
        setShowCreateModal(true);
    };

    const handleEditPlan = (plan) => {
        setEditingPlan(plan);
        setFormData({
            planName: plan.planName,
            planDescription: `Description for ${plan.planName}`,
            duration: plan.duration.split(' ')[0],
            durationUnit: plan.duration.split(' ')[1] || 'Weeks',
            price: plan.price.replace('$', ''),
            currency: 'USD',
            appStoreId: `app_${plan.id}`,
            playStoreId: `play_${plan.id}`,
            features: {
                freeMessages: '50',
                unlimitedLikes: 'Yes',
                travelMode: 'Yes',
                advancedFilters: 'Limited'
            }
        });
        setCurrentModalStep(1);
        setShowCreateModal(true);
        setOpenDropdown(null);
    };

    const handleDeletePlan = (plan) => {
        setSelectedPlan(plan);
        setOpenDropdown(null);
        setShowDeleteModal(true);
    };

    const handleActivatePlan = (plan) => {
        setSelectedPlan(plan);
        setOpenDropdown(null);
        setShowActivateModal(true);
    };

    const handleDeactivatePlan = (plan) => {
        setSelectedPlan(plan);
        setOpenDropdown(null);
        setShowDeactivateModal(true);
    };

    const handleNextStep = () => {
        setCurrentModalStep(2);
    };

    const handlePreviousStep = () => {
        setCurrentModalStep(1);
    };

    const handleFormSubmit = () => {
        console.log('Form submitted:', formData);
        setShowCreateModal(false);
        setCurrentModalStep(1);
        setEditingPlan(null);
    };

    const confirmDelete = () => {
        console.log('Deleting plan:', selectedPlan);
        setShowDeleteModal(false);
        setSelectedPlan(null);
    };

    const confirmActivate = () => {
        console.log('Activating plan:', selectedPlan);
        setShowActivateModal(false);
        setSelectedPlan(null);
    };

    const confirmDeactivate = () => {
        console.log('Deactivating plan:', selectedPlan);
        setShowDeactivateModal(false);
        setSelectedPlan(null);
    };

    const handleInputChange = (field, value) => {
        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({ ...prev, [field]: value }));
        }
    };

    const renderModalContent = () => {
        if (currentModalStep === 1) {
            return (
                <div className="space-y-6">
                    {/* Plan Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Plan Name*
                        </label>
                        <input
                            type="text"
                            value={formData.planName}
                            onChange={(e) => handleInputChange('planName', e.target.value)}
                            placeholder="Enter"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    {/* Plan Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Plan Description*
                        </label>
                        <textarea
                            value={formData.planDescription}
                            onChange={(e) => handleInputChange('planDescription', e.target.value)}
                            placeholder="Enter"
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        />
                    </div>

                    {/* Duration */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Duration*
                        </label>
                        <div className="flex space-x-2">
                            <input
                                type="number"
                                value={formData.duration}
                                onChange={(e) => handleInputChange('duration', e.target.value)}
                                placeholder="Enter"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <div className="relative">
                                <select
                                    value={formData.durationUnit}
                                    onChange={(e) => handleInputChange('durationUnit', e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                                >
                                    {durationUnits.map(unit => (
                                        <option key={unit} value={unit}>{unit}</option>
                                    ))}
                                </select>
                                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                            </div>
                        </div>
                    </div>

                    {/* Price */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Price*
                        </label>
                        <div className="flex space-x-2">
                            <input
                                type="number"
                                value={formData.price}
                                onChange={(e) => handleInputChange('price', e.target.value)}
                                placeholder="Enter"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <div className="relative">
                                <select
                                    value={formData.currency}
                                    onChange={(e) => handleInputChange('currency', e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-8"
                                >
                                    {currencies.map(currency => (
                                        <option key={currency} value={currency}>{currency}</option>
                                    ))}
                                </select>
                                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                            </div>
                        </div>
                    </div>

                    {/* AppStore ID */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            AppStore ID*
                        </label>
                        <input
                            type="text"
                            value={formData.appStoreId}
                            onChange={(e) => handleInputChange('appStoreId', e.target.value)}
                            placeholder="Enter"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    {/* PlayStore ID */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            PlayStore ID*
                        </label>
                        <input
                            type="text"
                            value={formData.playStoreId}
                            onChange={(e) => handleInputChange('playStoreId', e.target.value)}
                            placeholder="Enter"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                </div>
            );
        } else {
            return (
                <div className="space-y-6">
                    {/* Number of free messages for Men */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Number of free messages for Men*
                        </label>
                        <input
                            type="number"
                            value={formData.features.freeMessages}
                            onChange={(e) => handleInputChange('features.freeMessages', e.target.value)}
                            placeholder="Enter"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    {/* Unlimited Likes */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Unlimited Likes*
                        </label>
                        <div className="relative">
                            <select
                                value={formData.features.unlimitedLikes}
                                onChange={(e) => handleInputChange('features.unlimitedLikes', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                            >
                                <option value="">Select</option>
                                {featureOptions.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        </div>
                    </div>

                    {/* Travel Mode */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Travel Mode*
                        </label>
                        <div className="relative">
                            <select
                                value={formData.features.travelMode}
                                onChange={(e) => handleInputChange('features.travelMode', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                            >
                                <option value="">Select</option>
                                {featureOptions.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        </div>
                    </div>

                    {/* Advanced Filters */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Advanced Filters*
                        </label>
                        <div className="relative">
                            <select
                                value={formData.features.advancedFilters}
                                onChange={(e) => handleInputChange('features.advancedFilters', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                            >
                                <option value="">Select</option>
                                {featureOptions.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        </div>
                    </div>
                </div>
            );
        }
    };

    return (
        <div className="flex min-h-screen bg-gray-50">
            {/* Main Content */}
            <div className="flex-1 flex flex-col">
                {/* Breadcrumb */}
                <div className="bg-white border-b border-gray-200 px-8 py-4">
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <span>Dashboard</span>
                        <ChevronRight className="w-4 h-4" />
                        <span className="text-gray-900">Subscription Plans</span>
                    </div>
                </div>

                {/* Page Content */}
                <div className="flex-1 px-1 py-3">
                    <div className="max-w-7xl">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 mb-2">Subscription Plans</h1>
                                <p className="text-gray-600">All the subscription plans created for the app users are listed here.</p>
                            </div>
                            <button
                                onClick={handleCreatePlan}
                                className="px-6 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors font-medium"
                            >
                                Create
                            </button>
                        </div>

                        {/* Tabs */}
                        <div className="flex space-x-8 mb-6">
                            {['Active', 'Inactive', 'Deleted'].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`pb-2 border-b-2 font-medium transition-colors ${activeTab === tab
                                            ? 'border-gray-900 text-gray-900'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>

                        {/* Search */}
                        <div className="mb-6">
                            <div className="relative max-w-md">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder="Search by name"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>

                        {/* Table */}
                        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="w-12 px-6 py-3 text-left">
                                            <input
                                                type="checkbox"
                                                checked={selectedPlans.size === filteredPlans.length && filteredPlans.length > 0}
                                                onChange={(e) => handleSelectAll(e.target.checked)}
                                                className="rounded border-gray-300"
                                            />
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Plan Name
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Created on
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Price
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Duration
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Features
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            No of Users
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredPlans.map((plan) => (
                                        <tr key={plan.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedPlans.has(plan.id)}
                                                    onChange={(e) => handleSelectPlan(plan.id, e.target.checked)}
                                                    className="rounded border-gray-300"
                                                />
                                            </td>
                                            <td className="px-6 py-4 text-sm font-medium text-gray-900">{plan.planName}</td>
                                            <td className="px-6 py-4 text-sm text-gray-900">{plan.createdOn}</td>
                                            <td className="px-6 py-4 text-sm text-gray-900">{plan.price}</td>
                                            <td className="px-6 py-4 text-sm text-gray-900">{plan.duration}</td>
                                            <td className="px-6 py-4 text-sm text-gray-900">{plan.features}</td>
                                            <td className="px-6 py-4 text-sm text-gray-900">{plan.noOfUsers}</td>
                                            <td className="px-6 py-4 text-sm text-gray-400">
                                                <div className="relative" ref={openDropdown === plan.id ? dropdownRef : null}>
                                                    <button
                                                        onClick={() => toggleDropdown(plan.id)}
                                                        className="hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                                                    >
                                                        <MoreHorizontal className="w-5 h-5" />
                                                    </button>

                                                    {openDropdown === plan.id && (
                                                        <div className="absolute right-0 top-8 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                                                            <div className="py-1">
                                                                <button
                                                                    onClick={() => handleEditPlan(plan)}
                                                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                                                                >
                                                                    <Edit className="w-4 h-4" />
                                                                    <span>Edit</span>
                                                                </button>
                                                                {plan.status === 'Active' ? (
                                                                    <button
                                                                        onClick={() => handleDeactivatePlan(plan)}
                                                                        className="w-full px-4 py-2 text-left text-sm text-orange-600 hover:bg-orange-50 flex items-center space-x-2"
                                                                    >
                                                                        <Pause className="w-4 h-4" />
                                                                        <span>Make Inactive</span>
                                                                    </button>
                                                                ) : (
                                                                    <button
                                                                        onClick={() => handleActivatePlan(plan)}
                                                                        className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50 flex items-center space-x-2"
                                                                    >
                                                                        <Play className="w-4 h-4" />
                                                                        <span>Make Active</span>
                                                                    </button>
                                                                )}
                                                                <button
                                                                    onClick={() => handleDeletePlan(plan)}
                                                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                    <span>Delete</span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Pagination */}
                            <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                                <div className="text-sm text-gray-500">
                                    {selectedPlans.size} of {filteredPlans.length} row(s) selected.
                                </div>
                                <div className="flex items-center space-x-4">
                                    <div className="flex items-center space-x-2">
                                        <span className="text-sm text-gray-700">Rows per page</span>
                                        <select
                                            value={rowsPerPage}
                                            onChange={(e) => setRowsPerPage(Number(e.target.value))}
                                            className="border border-gray-300 rounded px-2 py-1 text-sm"
                                        >
                                            <option value={10}>10</option>
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                        </select>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <span className="text-sm text-gray-700">Page 1 of 10</span>
                                        <button className="p-1 hover:bg-gray-100 rounded">
                                            <ChevronsLeft className="w-4 h-4" />
                                        </button>
                                        <button className="p-1 hover:bg-gray-100 rounded">
                                            <ChevronLeft className="w-4 h-4" />
                                        </button>
                                        <button className="p-1 hover:bg-gray-100 rounded">
                                            <ChevronRight className="w-4 h-4" />
                                        </button>
                                        <button className="p-1 hover:bg-gray-100 rounded">
                                            <ChevronsRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Create/Edit Plan Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900">
                                        {editingPlan ? 'Edit Subscription Plan' : 'Create Subscription Plan'}
                                    </h2>
                                    <p className="text-sm text-gray-500 mt-1">
                                        Create a subscription plan for users to access limited features.
                                    </p>
                                </div>
                                <button
                                    onClick={() => setShowCreateModal(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            {/* Tab Navigation */}
                            <div className="flex border-b border-gray-200 mb-6">
                                <button
                                    onClick={() => setCurrentModalStep(1)}
                                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${currentModalStep === 1
                                            ? 'border-gray-900 text-gray-900'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    Basic Details
                                </button>
                                <button
                                    onClick={() => setCurrentModalStep(2)}
                                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${currentModalStep === 2
                                            ? 'border-gray-900 text-gray-900'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    Features
                                </button>
                            </div>

                            {/* Modal Content */}
                            {renderModalContent()}

                            {/* Modal Footer */}
                            <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                                <div>
                                    {currentModalStep === 2 && (
                                        <button
                                            onClick={handlePreviousStep}
                                            className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                                        >
                                            Previous
                                        </button>
                                    )}
                                </div>
                                <div className="flex space-x-3">
                                    <button
                                        onClick={() => setShowCreateModal(false)}
                                        className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    {currentModalStep === 1 ? (
                                        <button
                                            onClick={handleNextStep}
                                            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                                        >
                                            Next
                                        </button>
                                    ) : (
                                        <button
                                            onClick={handleFormSubmit}
                                            className="px-6 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors"
                                        >
                                            {editingPlan ? 'Update' : 'Create'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <div className="flex items-center space-x-3 mb-4">
                            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                                <Trash2 className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">Delete Plan</h2>
                                <p className="text-sm text-gray-600">This action cannot be undone</p>
                            </div>
                        </div>

                        <p className="text-sm text-gray-600 mb-6">
                            Are you sure you want to delete the plan <strong>{selectedPlan?.planName}</strong>?
                            This will permanently remove the subscription plan and all associated data.
                        </p>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                            >
                                Delete Plan
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Activate Confirmation Modal */}
            {showActivateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <div className="flex items-center space-x-3 mb-4">
                            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                <Play className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">Activate Plan</h2>
                                <p className="text-sm text-gray-600">Make this plan available to users</p>
                            </div>
                        </div>

                        <p className="text-sm text-gray-600 mb-6">
                            Are you sure you want to activate the plan <strong>{selectedPlan?.planName}</strong>?
                            This will make the subscription plan available for users to purchase.
                        </p>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowActivateModal(false)}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmActivate}
                                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                            >
                                Activate Plan
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Deactivate Confirmation Modal */}
            {showDeactivateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <div className="flex items-center space-x-3 mb-4">
                            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                                <Pause className="w-5 h-5 text-orange-600" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">Deactivate Plan</h2>
                                <p className="text-sm text-gray-600">Make this plan unavailable to users</p>
                            </div>
                        </div>

                        <p className="text-sm text-gray-600 mb-6">
                            Are you sure you want to deactivate the plan <strong>{selectedPlan?.planName}</strong>?
                            This will make the subscription plan unavailable for new purchases, but existing subscribers will keep their access.
                        </p>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowDeactivateModal(false)}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDeactivate}
                                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
                            >
                                Deactivate Plan
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SubscriptionPlansManagement;